import { Grid } from "@mui/material";
import { FieldArrayWithId, useFieldArray, useFormContext } from "react-hook-form";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { ChangeEvent, useRef, useState } from "react";
import { toast } from "react-toastify";
import { RHFCellTextField } from "../../../../Forms/RHFCellTextField";
import { RhfControlledDataTable } from "../../../../Layout/RhfControlledTable/RhfControlledDataTable";
import { RhfControlledTableColumnDefinition } from "../../../../Layout/RhfControlledTable/RhfControlledTableColumnDefinition";
import { CountrySelect } from "../../../../Locations/CountrySelect";
import { BidPack } from "../../BidPack";
import { BidPackOriginRegion, defaultBidPackOriginRegion } from "../../BidPackOriginRegion";
import {
  extractOriginRegionDataFromExcel,
  updateGridDataFromExcel,
} from "./BidPackOriginRegionUpload";
import { useFetchCountries } from "../../../../Locations/useFetchCountries";
import { isArrayUndefinedOrEmpty, mapKeyToArray } from "../../../../Utils/arrays";
import { ResetFilters } from "../../../../Layout/RhfControlledTable/ResetFilters";
import { RhfControlledDataTableFilter } from "../../../../Layout/RhfControlledTable/RhfControlledDataTableFilter";
import { mapReactHookFormIndizesToGivenFieldArray } from "../../../../Utils/reactHookFormUtils";
import { RhfCountryMultiSelect } from "../../../../Layout/ReactHookForm/RhfCountryMultiSelect";
import { useFetchGeoLocationSelectionItems } from "../../../../Locations/useFetchGeoLocationSelectionItems";
import { supplierUrls } from "../../../../URL/ApplicationUrls";
import { RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch } from "../../../../Supplier/Form/RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch";

export const BidPackOriginRegionForm = () => {
  const {
    control,
    getValues,
    formState: { disabled: isFormDisabled },
  } = useFormContext<BidPack>();
  const { fields, prepend, remove, replace } = useFieldArray<BidPack>({
    control,
    name: "OriginRegions",
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const { data: countries } = useFetchCountries();

  const originRegions = getValues("OriginRegions");
  const bidPackId = getValues("Id");

  const { data: initialSupplier } = useFetchGeoLocationSelectionItems({
    apiUrl: supplierUrls.initialSelectableGsdbs.apiUrl,
    selectedItems: mapKeyToArray(originRegions, "SupplierGsdb"),
    errorMessage: "Supplier",
    queryKeyName: "bidPackOriginRegion",
    queryKeySubName: "suppliers",
    id: bidPackId,
  });

  const bidPackOriginRegionsTableColumns: RhfControlledTableColumnDefinition<
    BidPack,
    "OriginRegions"
  >[] = [
    {
      fieldKey: "CountryId",
      isNumeric: false,
      headerText: "Country *",
      width: "20%",
      render: (fieldName) => (
        <CountrySelect control={control} name={fieldName} hideLabel disabled={isFormDisabled} />
      ),
    },
    {
      fieldKey: "ZipArea",
      isNumeric: false,
      headerText: "Postcode Area",
      width: "15%",
      render: (fieldName) => (
        <RHFCellTextField control={control} name={fieldName} disabled={isFormDisabled} />
      ),
    },
    {
      fieldKey: "ZipCode",
      isNumeric: false,
      headerText: "Postcode",
      width: "15%",
      render: (fieldName) => (
        <RHFCellTextField control={control} name={fieldName} disabled={isFormDisabled} />
      ),
    },
    {
      fieldKey: "SupplierGsdb",
      isNumeric: false,
      headerText: "Supplier GSDB",
      width: "25%",
      render: (fieldName) => (
        <RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch
          control={control}
          name={fieldName}
          initialSelectionItems={initialSupplier || []}
          apiUrl={supplierUrls.selectableGsdbsNew.apiUrl}
          errorMessage="An error occurred when retrieving the Supplier GSDBs"
          disabled={false}
        />
      ),
    },
    {
      fieldKey: "SelectedCrossingCountryIds",
      isNumeric: false,
      headerText: "Crossing Countries",
      width: "25%",
      render: (fieldName) => (
        <RhfCountryMultiSelect control={control} name={fieldName} disabled={isFormDisabled} />
      ),
    },
  ];

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }

    const selectedFile = e.target.files[0];
    const excelData = await extractOriginRegionDataFromExcel(selectedFile, countries).finally(
      () => {
        e.target.value = "";
      }
    );

    const updatedOriginRegionsGridData = updateGridDataFromExcel(
      excelData,
      getValues().OriginRegions
    );
    // update the field array with the new data
    replace(updatedOriginRegionsGridData);

    if (excelData.length > 0)
      toast.success("Import of origin regions completed. Duplicates were omitted.");
  };

  const [filteredFields, setFilteredFields] = useState<
    FieldArrayWithId<BidPack, "Destinations" | "OriginRegions">[] | undefined
  >();

  const filterBidPackOriginRegions = (
    fieldArray: FieldArrayWithId<BidPack, "Destinations" | "OriginRegions">[],
    propertyToFilterFor: "Postcode" | "PostcodeArea" | "SupplierGsdb",
    searchValue: string
  ) => {
    const foundFields = fieldArray.filter((field) => {
      const bidPackOriginRegionField = field as BidPackOriginRegion;

      if (propertyToFilterFor === "Postcode")
        return bidPackOriginRegionField.ZipCode?.startsWith(searchValue);

      if (propertyToFilterFor === "PostcodeArea")
        return bidPackOriginRegionField.ZipArea?.startsWith(searchValue);

      if (propertyToFilterFor === "SupplierGsdb")
        return bidPackOriginRegionField.SupplierGsdb?.startsWith(searchValue);

      return [];
    });

    if (isArrayUndefinedOrEmpty(foundFields)) {
      setFilteredFields(undefined);
    }

    const filteredFieldsWithIndexRelationToRhfFieldArray = mapReactHookFormIndizesToGivenFieldArray(
      {
        fieldsFromFormContext: fields,
        filteredFields: foundFields,
      }
    );

    setFilteredFields(filteredFieldsWithIndexRelationToRhfFieldArray);
  };

  const [resetFilters, setResetFilters] = useState<boolean>(false);

  return (
    <>
      <Grid container direction="row" pl={2} mt={1} spacing={0.5}>
        <Grid item>
          <RhfControlledDataTableFilter
            placeholder="Postcode Area"
            reset={resetFilters}
            handleSearch={(searchValue: string) =>
              filterBidPackOriginRegions(fields, "PostcodeArea", searchValue)
            }
          />
        </Grid>
        <Grid item>
          <RhfControlledDataTableFilter
            placeholder="Postcode"
            reset={resetFilters}
            handleSearch={(searchValue: string) =>
              filterBidPackOriginRegions(fields, "Postcode", searchValue)
            }
          />
        </Grid>
        <Grid item>
          <RhfControlledDataTableFilter
            placeholder="Supplier GSDB"
            reset={resetFilters}
            handleSearch={(searchValue: string) =>
              filterBidPackOriginRegions(fields, "SupplierGsdb", searchValue)
            }
          />
        </Grid>
        <ResetFilters
          handleClick={() => {
            setFilteredFields(undefined);
            setResetFilters(!resetFilters);
          }}
        />
      </Grid>
      <Grid item xs={12} container direction="column" style={{ paddingTop: "4px" }}>
        <RhfControlledDataTable
          fieldArrayName="OriginRegions"
          columns={bidPackOriginRegionsTableColumns}
          defaultForNewRow={defaultBidPackOriginRegion}
          rows={filteredFields ?? fields}
          insertRow={prepend}
          removeRow={remove}
          additionalToolbarItems={[
            {
              label: "UPLOAD",
              icon: <UploadFileIcon />,
              onClick: () => {
                inputRef?.current?.click();
              },
              disabled: isFormDisabled,
            },
          ]}
        />
        <input type="file" ref={inputRef} onChange={handleFileChange} style={{ display: "none" }} />
      </Grid>
    </>
  );
};
